/**
 * The public card of a SEALED company (Phase 119-07).
 *
 * A company whose last founder left is closed, acquired or bankrupt, and its
 * data is sealed: no client reads `startup_profiles` or its content tables for
 * it, so the `startup:startup_profiles(*)` embed on an investor's own dealflow /
 * thread / submission row comes back NULL. Owner decision #2 keeps the company
 * on the platform with its status, latest score and score history — this
 * module is how the app gets exactly that, and nothing else:
 *
 *   company_public_card(ids)        → a card per SEALED id (none for an
 *                                     operating, unknown or someone else's
 *                                     private company — so a returned card IS
 *                                     the "this company is sealed" signal)
 *   closed_company_directory(search) → the directory's list of closed companies
 *
 * Both are SECURITY DEFINER projections that return validated numbers and
 * fixed labels only (supabase/tests/company_public_card_test.sql). This module
 * parses them strictly anyway: a field of the wrong type is dropped, never
 * rendered.
 */

import { useQuery } from '@tanstack/react-query';
import { rpc } from '@/lib/supabase/rpc';
import { useAuth } from '@/lib/supabase';
import { isBackendEnabled, isMockMode } from '@/lib/config/app-mode';

// -----------------------------------------------------------------------------
// Shapes
// -----------------------------------------------------------------------------

// The pure vocabulary lives in ./lifecycle (importable without the client).
import { LIFECYCLE_LABEL, isLifecycleStatus, type LifecycleStatus } from './lifecycle';
export { LIFECYCLE_STATUSES, LIFECYCLE_LABEL, isLifecycleStatus, type LifecycleStatus } from './lifecycle';

export type EvidenceLevel = 'verified' | 'partially-verified' | 'unverified';

export interface CardDimension {
  id: string;
  rawScore: number | null;
  evidenceLevel: EvidenceLevel | null;
}

export interface CardScorePoint {
  /** 'YYYY-MM' */
  month: string;
  finalScore: number | null;
  fourAxis: Partial<Record<'ventureQuality' | 'readiness' | 'risk' | 'evidenceQuality', number>>;
  dimensions: CardDimension[];
  methodologyVersion: string | null;
}

export interface CompanyPublicCard {
  startupId: string;
  name: string | null;
  logoUrl: string | null;
  scoringDomain: string | null;
  scoringStage: string | null;
  status: LifecycleStatus;
  /** 'YYYY-Qn' */
  declaredQuarter: string | null;
  latest: CardScorePoint | null;
  history: CardScorePoint[];
}

export interface ClosedDirectoryEntry {
  directoryId: string;
  startupId: string;
  name: string;
  logoUrl: string | null;
  status: LifecycleStatus;
}

// -----------------------------------------------------------------------------
// Parsers (pure; exported for tests)
// -----------------------------------------------------------------------------

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

const AXES = ['ventureQuality', 'readiness', 'risk', 'evidenceQuality'] as const;
const LEVELS: readonly string[] = ['verified', 'partially-verified', 'unverified'];

export function parseScorePoint(v: unknown): CardScorePoint | null {
  const p = rec(v);
  const month = str(p.month);
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return null;
  const axes = rec(p.fourAxis);
  const fourAxis: CardScorePoint['fourAxis'] = {};
  for (const a of AXES) {
    const n = num(axes[a]);
    if (n !== null) fourAxis[a] = n;
  }
  const dimensions: CardDimension[] = Array.isArray(p.dimensions)
    ? p.dimensions.flatMap((d) => {
        const r = rec(d);
        const id = str(r.id);
        if (!id) return [];
        const level = typeof r.evidenceLevel === 'string' && LEVELS.includes(r.evidenceLevel)
          ? (r.evidenceLevel as EvidenceLevel)
          : null;
        return [{ id, rawScore: num(r.rawScore), evidenceLevel: level }];
      })
    : [];
  return {
    month,
    finalScore: num(p.finalScore),
    fourAxis,
    dimensions,
    methodologyVersion: str(p.methodologyVersion),
  };
}

/** One RPC row → a card, or null when the row is not a card (no id / status). */
export function parseCompanyPublicCard(row: unknown): CompanyPublicCard | null {
  const r = rec(row);
  const startupId = str(r.startup_id);
  if (!startupId) return null;
  // A row exists only for a sealed company; an unexpected status is still
  // sealed, shown as the default (the server's own fallback, 119-03).
  const status: LifecycleStatus = isLifecycleStatus(r.lifecycle_status) ? r.lifecycle_status : 'closed';
  const history = Array.isArray(r.history)
    ? r.history.map(parseScorePoint).filter((p): p is CardScorePoint => p !== null)
    : [];
  const logo = str(r.logo_url);
  return {
    startupId,
    name: str(r.name),
    logoUrl: logo && logo.startsWith('https://') ? logo : null,
    scoringDomain: str(r.scoring_domain),
    scoringStage: str(r.scoring_stage),
    status,
    declaredQuarter: typeof r.declared_quarter === 'string' && /^\d{4}-Q[1-4]$/.test(r.declared_quarter)
      ? r.declared_quarter
      : null,
    latest: parseScorePoint(r.latest) ?? history[history.length - 1] ?? null,
    history,
  };
}

export function parseClosedDirectoryEntry(row: unknown): ClosedDirectoryEntry | null {
  const r = rec(row);
  const directoryId = str(r.directory_id);
  const startupId = str(r.startup_id);
  const name = str(r.name);
  if (!directoryId || !startupId || !name) return null;
  const logo = str(r.logo_url);
  return {
    directoryId,
    startupId,
    name,
    logoUrl: logo && logo.startsWith('https://') ? logo : null,
    status: isLifecycleStatus(r.lifecycle_status) ? r.lifecycle_status : 'closed',
  };
}

// -----------------------------------------------------------------------------
// Display helpers (pure)
// -----------------------------------------------------------------------------

/** '2026-Q3' → 'Q3 2026'. */
export function formatQuarter(q: string | null): string | null {
  const m = q ? /^(\d{4})-(Q[1-4])$/.exec(q) : null;
  return m ? `${m[2]} ${m[1]}` : null;
}

/** "Closed · Q3 2026" / "Acquired". */
export function lifecycleLine(card: Pick<CompanyPublicCard, 'status' | 'declaredQuarter'>): string {
  const q = formatQuarter(card.declaredQuarter);
  return q ? `${LIFECYCLE_LABEL[card.status]} · ${q}` : LIFECYCLE_LABEL[card.status];
}

/** The final scores, oldest first, for a sparkline. */
export function historyScores(card: Pick<CompanyPublicCard, 'history'>): number[] {
  return card.history.map((p) => p.finalScore).filter((n): n is number => n !== null);
}

// -----------------------------------------------------------------------------
// Calls
// -----------------------------------------------------------------------------

/** company_public_card's own bound (22023 beyond). */
export const PUBLIC_CARD_MAX_IDS = 200;

/** Unique, lower-cased, sorted: one canonical set per request (and per cache key). */
export function canonicalIds(ids: readonly (string | null | undefined)[]): string[] {
  const set = new Set<string>();
  for (const id of ids) {
    if (typeof id === 'string' && id.trim()) set.add(id.trim().toLowerCase());
  }
  return [...set].sort();
}

/**
 * Cards for the sealed companies among `ids`, keyed by startup id. An id with
 * no entry is NOT sealed (or not visible) — the caller renders its own data.
 * Throws the RPC error (e.g. 42501 while an MFA step-up is pending): a failed
 * read is not "nothing is sealed".
 */
export async function fetchCompanyPublicCards(
  ids: readonly (string | null | undefined)[],
): Promise<Map<string, CompanyPublicCard>> {
  const all = canonicalIds(ids);
  const cards = new Map<string, CompanyPublicCard>();
  for (let i = 0; i < all.length; i += PUBLIC_CARD_MAX_IDS) {
    const chunk = all.slice(i, i + PUBLIC_CARD_MAX_IDS);
    const { data, error } = await rpc('company_public_card', { p_startup_ids: chunk });
    if (error) throw error;
    for (const row of (data ?? []) as unknown[]) {
      const card = parseCompanyPublicCard(row);
      if (card) cards.set(card.startupId.toLowerCase(), card);
    }
  }
  return cards;
}

export async function fetchClosedCompanyDirectory(
  search: string | null,
  opts: { limit?: number; offset?: number } = {},
): Promise<ClosedDirectoryEntry[]> {
  const q = search?.trim() ? search.trim().slice(0, 100) : undefined;
  const { data, error } = await rpc('closed_company_directory', {
    p_search: q,
    p_limit: opts.limit ?? 50,
    p_offset: opts.offset ?? 0,
  });
  if (error) throw error;
  return ((data ?? []) as unknown[])
    .map(parseClosedDirectoryEntry)
    .filter((e): e is ClosedDirectoryEntry => e !== null);
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

/** Sealed is near-static: a close happens once; a reopen is a rare admin act. */
const STALE_MS = 5 * 60_000;

/**
 * The cache key. Per user (an investor's own private sealed company has a card
 * only for them, so a key without the user could serve it to the next person
 * on the device) and per canonical id set, so every screen asking about the
 * same companies shares one entry.
 */
export const publicCardKeys = {
  all: ['company-public-cards'] as const,
  byIds: (userId: string | null | undefined, ids: readonly string[]) =>
    ['company-public-cards', userId ?? 'anonymous', ...ids] as const,
  closedDirectory: (userId: string | null | undefined, search: string) =>
    ['company-public-cards', 'closed-directory', userId ?? 'anonymous', search] as const,
};

export function publicCardsAvailable(): boolean {
  return isBackendEnabled() && !isMockMode();
}

/**
 * `useCompanyPublicCards(ids)` → `{ data: Map<startupId, card> }`.
 * Disabled (data undefined, not loading) when there is nothing to ask, no
 * signed-in user, or no backend (mock mode has no sealed companies).
 */
export function useCompanyPublicCards(ids: readonly (string | null | undefined)[], opts: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const canonical = canonicalIds(ids);
  return useQuery<Map<string, CompanyPublicCard>>({
    queryKey: publicCardKeys.byIds(user?.id, canonical),
    queryFn: () => fetchCompanyPublicCards(canonical),
    enabled: (opts.enabled ?? true) && !!user && canonical.length > 0 && publicCardsAvailable(),
    staleTime: STALE_MS,
    retry: 1,
  });
}

/** The card for one company, or null when it is not sealed. Shares the cache with lists. */
export function useCompanyPublicCard(startupId: string | null | undefined, opts: { enabled?: boolean } = {}) {
  const q = useCompanyPublicCards(startupId ? [startupId] : [], opts);
  const card = startupId ? q.data?.get(startupId.toLowerCase()) ?? null : null;
  return { ...q, card };
}

export function useClosedCompanyDirectory(search: string, opts: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const q = search.trim().slice(0, 100);
  return useQuery<ClosedDirectoryEntry[]>({
    queryKey: publicCardKeys.closedDirectory(user?.id, q),
    queryFn: () => fetchClosedCompanyDirectory(q || null),
    enabled: (opts.enabled ?? true) && !!user && publicCardsAvailable(),
    staleTime: STALE_MS,
    retry: 1,
  });
}
